// src/pages/ClientInvoiceDetail.js
// Componente para visualizar los detalles de una factura específica
// Muestra información completa, items y pagos asociados
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container,
    Typography,
    Box,
    Paper,
    Grid,
    Button,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    CircularProgress,
    Alert,
    Card,
    CardContent
} from '@mui/material';
import {
    Receipt as ReceiptIcon,
    Payment as PaymentIcon,
    ArrowBack as ArrowBackIcon,
    CalendarToday as CalendarIcon,
    Download as DownloadIcon,
    Check as CheckIcon,
    Warning as WarningIcon,
    RequestQuote as QuoteIcon,
    DateRange as DateRangeIcon,
    EventAvailable as EventAvailableIcon,
    WifiOff as WifiOffIcon
} from '@mui/icons-material';
import { format, isBefore } from 'date-fns';
import { es } from 'date-fns/locale';
import FiberLoader from '../components/common/FiberLoader';

// Componente para mostrar las fechas importantes con formato visual específico
const InvoiceDates = ({ invoice }) => {
    const today = new Date();
    const billingDate = new Date(invoice.billing_date || invoice.created_at);
    const paymentDueDate = new Date(invoice.payment_due_date || invoice.due_date);
    const dueDate = new Date(invoice.due_date);

    const isPaymentDuePassed = isBefore(paymentDueDate, today);
    const isDueDatePassed = isBefore(dueDate, today);

    return (
        <Box sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <DateRangeIcon sx={{ mr: 1 }} />
                Fechas Importantes
            </Typography>

            <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                    <Card variant="outlined">
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <CalendarIcon sx={{ mr: 1, color: 'primary.main' }} />
                                <Typography variant="subtitle2" color="textSecondary">
                                    Fecha de Emisión
                                </Typography>
                            </Box>
                            <Typography variant="h6">
                                {format(billingDate, 'dd MMMM yyyy', { locale: es })}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                                Día en que se generó la factura
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Card
                        variant="outlined"
                        sx={{
                            borderColor: isPaymentDuePassed ? 'error.main' : 'inherit',
                            bgcolor: isPaymentDuePassed ? 'rgba(244, 67, 54, 0.08)' : 'inherit'
                        }}
                    >
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <EventAvailableIcon
                                    sx={{
                                        mr: 1,
                                        color: isPaymentDuePassed ? 'error.main' : 'warning.main'
                                    }}
                                />
                                <Typography
                                    variant="subtitle2"
                                    color={isPaymentDuePassed ? "error" : "textSecondary"}
                                >
                                    Fecha Límite de Pago
                                </Typography>
                            </Box>
                            <Typography
                                variant="h6"
                                color={isPaymentDuePassed ? "error" : "inherit"}
                                sx={{ fontWeight: isPaymentDuePassed ? 'bold' : 'normal' }}
                            >
                                {format(paymentDueDate, 'dd MMMM yyyy', { locale: es })}
                            </Typography>
                            <Typography
                                variant="caption"
                                color={isPaymentDuePassed ? "error" : "textSecondary"}
                            >
                                {isPaymentDuePassed
                                    ? "¡Fecha límite vencida!"
                                    : "Último día para pagar sin recargos"}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={4}>
                    <Card
                        variant="outlined"
                        sx={{
                            borderColor: isDueDatePassed ? 'error.main' : 'inherit',
                            bgcolor: isDueDatePassed ? 'rgba(244, 67, 54, 0.08)' : 'inherit'
                        }}
                    >
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <WifiOffIcon
                                    sx={{
                                        mr: 1,
                                        color: isDueDatePassed ? 'error.main' : 'error.light'
                                    }}
                                />
                                <Typography
                                    variant="subtitle2"
                                    color={isDueDatePassed ? "error" : "textSecondary"}
                                >
                                    Fecha de Corte
                                </Typography>
                            </Box>
                            <Typography
                                variant="h6"
                                color={isDueDatePassed ? "error" : "inherit"}
                                sx={{ fontWeight: isDueDatePassed ? 'bold' : 'normal' }}
                            >
                                {format(dueDate, 'dd MMMM yyyy', { locale: es })}
                            </Typography>
                            <Typography
                                variant="caption"
                                color={isDueDatePassed ? "error" : "textSecondary"}
                            >
                                {isDueDatePassed
                                    ? "¡Servicio suspendido por falta de pago!"
                                    : "Día en que se suspenderá el servicio si no hay pago"}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Box>
    );
};

const ClientInvoiceDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [invoice, setInvoice] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchInvoiceDetails();
    }, [id]);

    const fetchInvoiceDetails = async () => {
        try {
            const token = localStorage.getItem('clientToken');
            const response = await fetch(`/api/client/invoices/${id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Error al obtener detalles de la factura');
            }

            const data = await response.json();
            setInvoice(data);
        } catch (err) {
            console.error('Error fetching invoice details:', err);
            setError(err.message || 'Error al cargar los detalles de la factura');
        } finally {
            setLoading(false);
        }
    };

    // Reemplazar la función handleDownloadInvoice en ClientInvoiceDetail.js

    const handleDownloadInvoice = () => {
        // Obtener el token
        const token = localStorage.getItem('clientToken');

        // Construir la URL
        const downloadUrl = `/api/client/invoices/${id}/download`;

        // Crear un elemento a temporal para la descarga
        const link = document.createElement('a');
        link.href = downloadUrl;

        // Añadir el token a la URL como parámetro de consulta
        if (token) {
            link.href = `${downloadUrl}?token=${token}`;
        }

        // Establecer la descarga como nueva pestaña con nombre de archivo
        link.target = '_blank';
        link.rel = 'noopener noreferrer';

        // Hacer clic en el enlace
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handlePayNow = () => {
        navigate('/client/payments', { state: { invoiceId: id } });
    };

    const getStatusChip = (status) => {
        switch (status) {
            case 'paid':
                return <Chip label="Pagada" color="success" size="small" icon={<CheckIcon />} />;
            case 'partially_paid':
                return (
                    <Chip 
                        label="Pago Parcial" 
                        size="small" 
                        sx={{ 
                            backgroundColor: 'rgba(249, 115, 22, 0.1)', 
                            color: '#ea580c', 
                            border: '1px solid rgba(249, 115, 22, 0.2)',
                            fontWeight: 600
                        }} 
                    />
                );
            case 'pending':
                return <Chip label="Pendiente" color="primary" size="small" />;
            case 'overdue':
                return <Chip label="Vencida" color="error" size="small" icon={<WarningIcon />} />;
            case 'promised':
                return <Chip label="Prometida" color="warning" size="small" icon={<CalendarIcon />} />;
            default:
                return <Chip label={status} size="small" />;
        }
    };

    if (loading) {
        return (
            <FiberLoader text="Cargando detalle del recibo..." subtext="Descargando información del comprobante de fibra óptica..." fullPage />
        );
    }

    if (error) {
        return (
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
                <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/client/payments')}
                >
                    Volver a Recibos
                </Button>
            </Container>
        );
    }

    if (!invoice) {
        return (
            <Container maxWidth="md" sx={{ py: 4 }}>
                <Alert severity="warning" sx={{ mb: 3 }}>
                    No se encontró la factura solicitada.
                </Alert>
                <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/client/payments')}
                >
                    Volver a Recibos
                </Button>
            </Container>
        );
    }

    // Calcular monto pendiente
    const totalAmount = parseFloat(invoice.amount);
    const paidAmount = parseFloat(invoice.paid_amount || 0);
    const pendingAmount = totalAmount - paidAmount;

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Button
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/client/payments')}
                sx={{ mb: 3 }}
            >
                Volver a Recibos
            </Button>

            <Paper sx={{ p: 4, mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                    <Box>
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                            {invoice.sunat_status === 'ACEPTADO' && invoice.sunat_number ? (
                                <Chip
                                    label={invoice.sunat_number.startsWith('F') ? 'Factura SUNAT' : 'Boleta SUNAT'}
                                    color="info"
                                    size="small"
                                    sx={{ fontWeight: 700 }}
                                />
                            ) : (
                                <Chip
                                    label="Recibo Interno"
                                    size="small"
                                    variant="outlined"
                                />
                            )}
                        </Box>
                        <Typography variant="h5" component="h1" gutterBottom fontWeight="bold">
                            <ReceiptIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
                            {invoice.sunat_status === 'ACEPTADO' && invoice.sunat_number
                                ? `Comprobante ${invoice.sunat_number}`
                                : `Recibo #${invoice.invoice_number}`}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Fecha de emisión: {format(new Date(invoice.billing_date || invoice.created_at), 'dd MMMM yyyy', { locale: es })}
                        </Typography>
                    </Box>
                    <Box>
                        {getStatusChip(invoice.status)}
                    </Box>
                </Box>

                <Divider sx={{ mb: 3 }} />

                <Grid container spacing={4} sx={{ mb: 4 }}>
                    <Grid item xs={12} md={4}>
                        <Card variant="outlined">
                            <CardContent>
                                <Typography variant="overline" color="textSecondary">Monto Total</Typography>
                                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
                                    S/ {totalAmount.toFixed(2)}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Card variant="outlined">
                            <CardContent>
                                <Typography variant="overline" color="textSecondary">Pagado</Typography>
                                <Typography variant="h5" sx={{ color: 'success.main' }}>
                                    S/ {paidAmount.toFixed(2)}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Card variant="outlined">
                            <CardContent>
                                <Typography variant="overline" color="textSecondary">Pendiente</Typography>
                                <Typography variant="h5" sx={{ color: pendingAmount > 0 ? 'error.main' : 'text.primary' }}>
                                    S/ {pendingAmount.toFixed(2)}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>

                {/* Mostrar componente de Fechas Importantes */}
                <InvoiceDates invoice={invoice} />

                {invoice.has_pending_payment && (
                    <Alert severity="warning" sx={{ mb: 4 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                            Reporte de pago en verificación
                        </Typography>
                        <Typography variant="body2">
                            Hemos recibido tu reporte de pago para esta factura y se encuentra actualmente en proceso de validación por nuestro equipo administrativo.
                        </Typography>
                    </Alert>
                )}

                <Typography variant="h6" gutterBottom>Detalles</Typography>

                <TableContainer component={Paper} variant="outlined" sx={{ mb: 4 }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Descripción</TableCell>
                                <TableCell align="right">Cantidad</TableCell>
                                <TableCell align="right">Precio Unitario</TableCell>
                                <TableCell align="right">Subtotal</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {invoice.items && invoice.items.length > 0 ? (
                                invoice.items.map((item, index) => (
                                    <TableRow key={index}>
                                        <TableCell>{item.description}</TableCell>
                                        <TableCell align="right">{item.quantity}</TableCell>
                                        <TableCell align="right">
                                            S/ {(parseFloat(item.amount) / item.quantity).toFixed(2)}
                                        </TableCell>
                                        <TableCell align="right">S/ {parseFloat(item.amount).toFixed(2)}</TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>
                                        {invoice.description || `Servicio de Internet Mensual - ${invoice.plan_name || 'Plan Asignado (20/20 Mbps)'}`}
                                    </TableCell>
                                    <TableCell align="right">1</TableCell>
                                    <TableCell align="right">S/ {totalAmount.toFixed(2)}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>S/ {totalAmount.toFixed(2)}</TableCell>
                                </TableRow>
                            )}
                            <TableRow>
                                <TableCell colSpan={3} align="right" sx={{ fontWeight: 'bold' }}>
                                    Total
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                                    S/ {totalAmount.toFixed(2)}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>

                {invoice.payments && invoice.payments.length > 0 && (
                    <>
                        <Typography variant="h6" gutterBottom>Historial de Pagos</Typography>
                        <TableContainer component={Paper} variant="outlined" sx={{ mb: 4 }}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Fecha</TableCell>
                                        <TableCell>Método</TableCell>
                                        <TableCell>Referencia</TableCell>
                                        <TableCell>Estado</TableCell>
                                        <TableCell align="right">Monto</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {invoice.payments.map((payment, index) => (
                                        <TableRow key={index}>
                                            <TableCell>
                                                {format(new Date(payment.payment_date), 'dd/MM/yyyy')}
                                            </TableCell>
                                            <TableCell>{payment.payment_method}</TableCell>
                                            <TableCell>{payment.payment_reference || '-'}</TableCell>
                                            <TableCell>
                                                {payment.status === 'completed' ? (
                                                    <Chip label="Completado" color="success" size="small" />
                                                ) : payment.status === 'pending' ? (
                                                    <Chip label="Pendiente" color="primary" size="small" />
                                                ) : (
                                                    <Chip label={payment.status} size="small" />
                                                )}
                                            </TableCell>
                                            <TableCell align="right">S/ {parseFloat(payment.amount).toFixed(2)}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </>
                )}

                {invoice.payment_promise_date && (
                    <Alert severity="info" sx={{ mb: 4 }}>
                        <Typography variant="subtitle2">Promesa de Pago</Typography>
                        <Typography variant="body2">
                            Has prometido pagar esta factura antes del {format(new Date(invoice.payment_promise_date), 'dd MMMM yyyy', { locale: es })}.
                        </Typography>
                    </Alert>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                    <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={handleDownloadInvoice}
                    >
                        Descargar PDF
                    </Button>

                    {pendingAmount > 0 && (
                        invoice.has_pending_payment ? (
                            <Button
                                variant="contained"
                                color="warning"
                                startIcon={<WarningIcon />}
                                disabled
                                sx={{
                                    bgcolor: 'rgba(224, 168, 0, 0.1) !important',
                                    color: '#856404 !important',
                                    border: '1px solid #ffeeba'
                                }}
                            >
                                Pago Reportado
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<PaymentIcon />}
                                onClick={handlePayNow}
                            >
                                Pagar Ahora
                            </Button>
                        )
                    )}
                </Box>
            </Paper>
        </Container>
    );
};

export default ClientInvoiceDetail;